import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Shell, TrustLine } from "@/components/public/PublicChrome";
import { Reveal } from "@/components/public/Reveal";
import { Shot } from "@/components/public/Shot";
import { HomeHero } from "@/components/public/HomeHero";
import { HomeTour, FeatureRow } from "@/components/public/HomeTour";
import { HomeHighlights, HomeMore, HomeFaq, HomeFinalCta } from "@/components/public/HomeSections";
import { Container, H2, PageCta, PageHeader, PlanPill } from "@/components/public/PageParts";
import { TierGrid, Passes, PricingDetails } from "@/components/public/PricingParts";
import { EXPLAIN_SHOT, FEATURES, MEMORY_SHOT, PRIVACY, STEPS } from "@/lib/publicContent";

export { TrustLine, PrivacyLine } from "@/components/public/PublicChrome";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");
const WIDE = "(min-width: 1280px) 1216px, 100vw";

export function PublicHome() {
  const { user } = useAuth();
  if (user) return <Navigate to="/app" replace />;
  return (
    <Shell>
      <HomeHero />
      <HomeHighlights />
      <HomeTour />
      <HomeMore />
      <HomeFaq />
      <HomeFinalCta />
    </Shell>
  );
}

function FeatureList() {
  return (
    <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12" data-testid="feature-list">
      {FEATURES.map((f, i) => (
        <li key={f.name} data-testid={`feature-${f.name.split(" ")[0].toLowerCase()}`}>
          <Reveal delay={(i % 3) * 70} className="border-t border-white/[0.08] pt-6">
            <span className="h-10 w-10 grid place-items-center rounded-xl bg-sky-400/10 text-sky-300 ring-1 ring-sky-400/20"><f.icon className="h-5 w-5" aria-hidden="true" /></span>
            <h2 className="font-display text-lg font-semibold tracking-tight mt-5">{f.name}</h2>
            <p className="text-sm text-neutral-400 mt-2 leading-relaxed">{f.text}</p>
            {f.plan && <PlanPill testid={`feature-${slug(f.name)}-plan`}>{f.plan}</PlanPill>}
          </Reveal>
        </li>
      ))}
    </ul>
  );
}

export function FeaturesPage() {
  return (
    <Shell title="Features">
      <PageHeader title="Features" testid="features-header"
        intro="Everything BrynAI does, in plain words. Most features work on every plan; the ones that need a paid plan say so." />
      <Container className="mt-14 sm:mt-20"><Shot {...EXPLAIN_SHOT} eager sizes={WIDE} testid="features-shot" /></Container>
      <Container className="mt-24 sm:mt-32"><FeatureList /></Container>
      <PageCta />
    </Shell>
  );
}

export function HowItWorksPage() {
  return (
    <Shell title="How it works">
      <PageHeader title="How it works" testid="how-header" />
      <Container className="mt-16 sm:mt-24 space-y-28 sm:space-y-40" testid="how-steps">
        {STEPS.map((s, i) => <FeatureRow key={s.id} item={s} index={i} heading="h2" testid={`step-${i + 1}`} />)}
      </Container>
      <PageCta />
    </Shell>
  );
}

export function PricingPage() {
  return (
    <Shell title="Pricing">
      <PageHeader title="Pricing" intro="Less than ChatGPT Plus, built for studying, not chat." introTestid="pricing-anchor" testid="pricing-header" />
      <Container className="mt-14 sm:mt-16">
        <TierGrid />
        <Passes />
        <PricingDetails />
        <div className="mt-8 space-y-1"><TrustLine /><p className="text-xs text-neutral-500" data-testid="pricing-stripe-note">Payments handled securely by Stripe.</p></div>
      </Container>
    </Shell>
  );
}

function AboutMemory() {
  return (
    <section id="memory" className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center" aria-labelledby="about-memory-title" data-testid="about-memory">
      <Reveal className="lg:col-span-5 space-y-5 text-neutral-300 leading-relaxed">
        <H2 id="about-memory-title" className="text-white">How memory works</H2>
        <p><b>Private memory</b> holds notes about you: what you're studying, the topics you find hard and the explanations that worked. Only you can see it, on the "What I know about you" page, and you can delete any of it.</p>
        <p>Nothing from your sessions is shared with other students or used to answer them.</p>
        <p>Your uploads and chats stay private to your account, and we never sell your data. <Link to="/privacy" className="text-sky-400 hover:underline" data-testid="about-privacy-link">Read the privacy page</Link>.</p>
      </Reveal>
      <Reveal delay={120} className="lg:col-span-7"><Shot {...MEMORY_SHOT} testid="about-memory-shot" /></Reveal>
    </section>
  );
}

export function AboutPage() {
  return (
    <Shell title="About">
      <PageHeader title="About BrynAI" testid="about-header" />
      <Container className="mt-8 sm:mt-10">
        <p className="max-w-3xl text-lg sm:text-xl text-neutral-200 leading-relaxed rise" data-testid="about-intro">BrynAI is an AI study tutor for students. It was built for the moment you're stuck: a homework question at 11pm, an exam tomorrow, or a pile of lecture slides you haven't opened. Instead of handing over an answer, it teaches the method step by step, so you can do the next one yourself.</p>
      </Container>
      <Container className="mt-24 sm:mt-32"><AboutMemory /></Container>
      <PageCta />
    </Shell>
  );
}

export function PrivacyPage() {
  return (
    <Shell title="Privacy">
      <PageHeader title="Privacy, in plain English" testid="privacy-header" />
      <Container className="mt-14 sm:mt-20">
        <div className="divide-y divide-white/[0.07] border-y border-white/[0.07]">
          {PRIVACY.map(([h, t], i) => (
            <Reveal key={h} delay={i * 60} className="grid lg:grid-cols-12 gap-3 lg:gap-10 py-8 sm:py-10" testid={`privacy-${slug(h)}`}>
              <h2 className="lg:col-span-4 font-display text-xl font-semibold tracking-tight">{h}</h2>
              <p className="lg:col-span-8 max-w-2xl text-neutral-400 leading-relaxed">{t}</p>
            </Reveal>
          ))}
        </div>
      </Container>
    </Shell>
  );
}
